import { Filter, FileDown, List } from "@dreamhub/designsystem/icons";
import { dealsModalMockData } from "@/assets/mockupData";
import DataTable from "@/components/controls/DataTable/DataTable";
import Modal from "@/components/controls/Modal";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import SearchInput from "@/components/ui/search-input";
import Text from "@/components/ui/text";
import { UserData } from "@/entities/users/types";
import React, {
  Dispatch,
  memo,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useTranslation } from "react-i18next";
import { formatFullDate } from "@/utils/datetime";
import { formatNumber } from "@/utils/numbers";
import { dealStatusColors } from "@/pages/deals/components/DealsTable/DealsTable";
import { DealConfidenceLevel, DealStatus } from "@/pages/deals/types";
import { LeadStatus } from "@/pages/leads/types";
import { getEnumTKey } from "@/utils/strings";
import ConfidenceLevelIcon from "@/components/controls/ConfidenceLevelIcon";
import { StatusBadge } from "@/components/ui/status-badge";
import { useDealStages } from "@/entities/settings/deal/hooks";
import { find, isEmpty, map } from "lodash";

interface Props {
  user: UserData | null;
  setUser: Dispatch<SetStateAction<UserData | null>>;
}

export type dealsDrilldownTableDataType = {
  displayName: string;
  status: LeadStatus;
  stage: string;
  stakeholdersScore: number;
  confidenceLevel: DealConfidenceLevel;
  closeDate: string;
  mrr: number;
}[];

const DealsDrilldownModal: React.FC<Props> = ({ user, setUser }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(user);
  const contentRef = useRef<HTMLDivElement>(null);
  const [minHeight, setMinHeight] = useState<number | undefined>(undefined);

  const stages = useDealStages().data?.stages;

  useEffect(() => {
    if (!isOpen) {
      setUser(null);
    }
  }, [isOpen]);

  useEffect(() => {
    setIsOpen(!isEmpty(user));
    setCurrentUser(user);
  }, [user]);

  useEffect(() => {
    if (contentRef.current && !minHeight) {
      const height = contentRef.current.offsetHeight;
      if (height > 0) {
        setMinHeight(height);
      }
    }
  }, [user, minHeight]);

  const columns = useMemo(
    () => [
      {
        header: "Deal name",
        accessorKey: "displayName",
        meta: {
          textAlign: "start"
        }
      },
      {
        header: "Status",
        accessorKey: "status",
        cell: ({ getValue }: { getValue: () => DealStatus }) => {
          const value = getValue();
          return (
            <StatusBadge statusColor={dealStatusColors[value]}>
              {t(
                getEnumTKey(
                  "stakeholders.dealsModal.table",
                  "status",
                  DealStatus,
                  value
                )
              )}
            </StatusBadge>
          );
        },
        meta: {
          textAlign: "start"
        }
      },
      {
        header: "Stage",
        accessorKey: "stage",
        cell: ({ getValue }: { getValue: () => string }) => {
          const value = getValue();
          const stage = find(stages, (stage) => stage.id === Number(value));
          return <div>{stage?.name || ""}</div>;
        },
        meta: {
          textAlign: "start"
        }
      },
      {
        header: "Key stakeholders mapping score",
        accessorKey: "stakeholdersScore",
        cell: ({ getValue }: { getValue: () => string }) => (
          <div className="flex items-center">
            <span className="mr-2">{getValue()}%</span>
            <div className="w-[75%] bg-gray-600 rounded-full h-1">
              <Progress
                value={Number(getValue())}
                indicatorClassname="border-r border-surface-elevation1"
              />
            </div>
          </div>
        ),
        meta: {
          textAlign: "start",
          maxWidth: "11rem"
        }
      },
      {
        header: "AE confidence level",
        accessorKey: "confidenceLevel",
        cell: ({ getValue }: { getValue: () => DealConfidenceLevel }) => {
          const value = getValue();
          return (
            <div className="flex items-center min-w-36 h-6">
              <ConfidenceLevelIcon value={value} />
              <span className="ml-2">
                {t(getEnumTKey("deals", "levels", DealConfidenceLevel, value))}
              </span>
            </div>
          );
        },
        meta: {
          textAlign: "start"
        }
      },
      {
        header: "AE expected close date",
        accessorKey: "closeDate",
        cell: ({ getValue }: { getValue: () => string }) => {
          const value = getValue();
          return <div>{formatFullDate(value)}</div>;
        },
        meta: {
          textAlign: "start"
        }
      },
      {
        header: "MRR",
        accessorKey: "mrr",
        cell: ({ getValue }: { getValue: () => string }) => {
          const mrr = Number(getValue());
          const { value, currencySign } = formatNumber(mrr, 0, true, false);
          return <div>{`${currencySign}${value}`}</div>;
        },
        meta: {
          textAlign: "start"
        }
      }
    ],
    [stages]
  );

  const actions = useMemo(
    () => [
      { name: "filter", icon: <Filter /> },
      { name: "export", icon: <FileDown /> },
      { name: "viewInList", icon: <List /> }
    ],
    []
  );

  return (
    <Modal
      open={isOpen}
      setIsOpen={setIsOpen}
      contentClassname="w-[90%]"
      title={
        <div className="flex flex-col gap-3">
          <Text as="div" variant="lgSubtitleH1">
            {t("stakeholders.dealsModal.title")}
          </Text>
          <Text as="div" variant="lgBodySecondary">
            {currentUser?.displayName || ""}
          </Text>
        </div>
      }
      content={
        <div
          ref={contentRef}
          className="flex flex-col h-full w-full"
          style={{ minHeight: minHeight ? `${minHeight}px` : "30rem" }}
        >
          <div className="w-full h-14 flex justify-between px-2 py-[6px]">
            <SearchInput placeholder={t("search")} />
            <div className="flex gap-2">
              {map(actions, (action, index) => (
                <Button
                  key={action.name + index}
                  variant="secondary"
                  className="flex h-fit"
                >
                  {action.icon}
                  {t(`stakeholders.dealsModal.actions.${action.name}`)}
                </Button>
              ))}
            </div>
          </div>
          <div className="w-full">
            <DataTable
              columns={columns}
              data={dealsModalMockData}
              initialSortBy={[{ id: "stakeholdersScore", desc: false }]}
            />
          </div>
        </div>
      }
    />
  );
};

export default memo(DealsDrilldownModal);
